/**
 * SketchPoint - Control Point for Paths
 * Ported from cc.sketchchair.sketch.SketchPoint.java
 *
 * Represents a control point on a path with optional Bezier control points.
 * Extends Vec2D to add Bezier curve support.
 */

import { Vec2D } from './geometry/Vec2D.js';

export class SketchPoint extends Vec2D {
    /**
     * Create a new sketch point
     * @param {number|Vec2D|Object} x - X coordinate, Vec2D, or JSON data
     * @param {number} [y] - Y coordinate
     */
    constructor(x, y) {
        // Handle different constructor signatures
        if (typeof x === 'object' && x !== null && !(x instanceof Vec2D)) {
            // JSON/Object constructor
            super(x.x || 0, x.y || 0);

            // Load control points from JSON
            if (x.c1x !== undefined && x.c1y !== undefined) {
                this.controlPoint1 = new Vec2D(x.c1x, x.c1y);
            } else {
                this.controlPoint1 = null;
            }

            if (x.c2x !== undefined && x.c2y !== undefined) {
                this.controlPoint2 = new Vec2D(x.c2x, x.c2y);
            } else {
                this.controlPoint2 = null;
            }
        } else if (x instanceof Vec2D) {
            // Vec2D constructor
            super(x.x, x.y);
            this.controlPoint1 = null;
            this.controlPoint2 = null;
        } else {
            // (x, y) constructor
            super(x || 0, y || 0);
            this.controlPoint1 = null;
            this.controlPoint2 = null;
        }

        // UI state
        this.isOver = false;
        this.smooth = false;
    }

    /**
     * Clone this point
     * @returns {SketchPoint} New cloned point
     */
    clone() {
        const returnPoint = new SketchPoint(this.x, this.y);

        if (this.controlPoint1 !== null) {
            returnPoint.controlPoint1 = new Vec2D(
                this.controlPoint1.x,
                this.controlPoint1.y
            );
        }

        if (this.controlPoint2 !== null) {
            returnPoint.controlPoint2 = new Vec2D(
                this.controlPoint2.x,
                this.controlPoint2.y
            );
        }

        return returnPoint;
    }

    /**
     * Check if this point has Bezier control points
     * @returns {boolean} True if has control points
     */
    containsBezier() {
        return this.controlPoint1 !== null || this.controlPoint2 !== null;
    }

    /**
     * Get first control point (returns this point if no control point set)
     * @returns {Vec2D} Control point or this point
     */
    getControlPoint1() {
        if (this.controlPoint1 === null) {
            return new Vec2D(this.x, this.y);
        }
        return this.controlPoint1;
    }

    /**
     * Get second control point (returns this point if no control point set)
     * @returns {Vec2D} Control point or this point
     */
    getControlPoint2() {
        if (this.controlPoint2 === null) {
            return new Vec2D(this.x, this.y);
        }
        return this.controlPoint2;
    }

    /**
     * Remove Bezier control points
     */
    removeBezier() {
        this.controlPoint1 = null;
        this.controlPoint2 = null;
    }

    /**
     * Convert to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        const json = {
            x: this.x,
            y: this.y
        };

        if (this.controlPoint1 !== null) {
            json.c1x = this.controlPoint1.x;
            json.c1y = this.controlPoint1.y;
        }

        if (this.controlPoint2 !== null) {
            json.c2x = this.controlPoint2.x;
            json.c2y = this.controlPoint2.y;
        }

        return json;
    }

    /**
     * Create from JSON
     * @param {Object} data - JSON data
     * @returns {SketchPoint} New point
     */
    static fromJSON(data) {
        return new SketchPoint(data);
    }

    /**
     * String representation
     * @returns {string} String
     */
    toString() {
        let str = `SketchPoint{x: ${this.x}, y: ${this.y}`;

        if (this.controlPoint1 !== null) {
            str += `, c1: (${this.controlPoint1.x}, ${this.controlPoint1.y})`;
        }

        if (this.controlPoint2 !== null) {
            str += `, c2: (${this.controlPoint2.x}, ${this.controlPoint2.y})`;
        }

        return str + '}';
    }
}
